import type { MiniGameEnd, PlayApi, SlotDemoScene } from '@goodtime/engine';
import type { AnyPlayActResult, AnyPlayView, PlayOffer } from '@goodtime/shared';
import { anyPlayFromConflict, fetchPlay, playAct } from '../../api/plays';
import { withRetry } from '../../api/road';

/**
 * The server calls for one play, as a game's screen makes them. An act
 * retries when the connection drops (same key, so it's answered once);
 * after a failure, `recover` hands back the play from the 409, or fetches
 * it.
 */
export function playApi(playId: string, sessionId: string): PlayApi {
  let conflict: AnyPlayView | null = null;
  return {
    act: async (step, action, arg) => {
      conflict = null;
      try {
        return await withRetry(() => playAct<AnyPlayActResult>(playId, { sessionId, step, action, arg }));
      } catch (err) {
        conflict = anyPlayFromConflict<AnyPlayView>(err);
        throw err;
      }
    },
    recover: async () => conflict ?? (await fetchPlay<AnyPlayView>(playId, sessionId).catch(() => null)),
  };
}

/**
 * The client registry: how each kind of play the server offers opens. The
 * road's mini-games play on the slot canvas (the engine's
 * MINI_GAME_SCREENS); a kind the scene can't play ends at once with
 * nothing paid here, and the server settles it when it's left. A friend's
 * game is under `title`, its own name on the road.
 */
export function playGame(scene: SlotDemoScene, offer: PlayOffer, sessionId: string, title?: string): Promise<MiniGameEnd> {
  if (!scene.playsMiniGame(offer.kind)) return Promise.resolve({ prize: null });
  return scene.playMiniGame(offer, playApi(offer.playId, sessionId), title);
}
